import { Box, Typography } from '@mui/material';
import { useNavigate, useRouteError, isRouteErrorResponse } from 'react-router';
import { osirisTokens } from '@/theme/tokens';
import emptyBgImage from '@/assets/empty_bg.jpg';

interface ErrorViewProps {
  /** HTTP-style code, e.g. "404" / "500". */
  code: string;
  /** Short status label shown after "STATUS:". */
  statusLabel: string;
  /** Large descriptive line under the code. */
  description: string;
  /** Accent used for the status label + reticle (purple for 404, red for 500). */
  tone: string;
}

const CORDS = [
  { label: 'Latitude', value: '46.4052° N' },
  { label: 'Longitude', value: '30.7496° E' },
  { label: 'Area', value: 'SECTOR 7' },
];

function CornerBracket({ sx, color }: { sx: object; color: string }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        width: 24,
        height: 24,
        borderColor: color,
        borderStyle: 'solid',
        ...sx,
      }}
    />
  );
}

export function ErrorView({ code, statusLabel, description, tone }: ErrorViewProps) {
  const navigate = useNavigate();

  const buttonLabelSx = {
    fontFamily: osirisTokens.fonts.display,
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: osirisTokens.text.primary,
  };

  return (
    <Box
      className={`error-page error-page--${code}`}
      sx={{
        position: 'relative',
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: osirisTokens.bg.page,
      }}
    >
      {/* Topo-contour background — the design's own texture (Figma To Do 28458:21481) */}
      <Box
        component="img"
        src={emptyBgImage}
        alt=""
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: 0.15,
        }}
      />
      <Box aria-hidden sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(26, 26, 26, 0.5)' }} />
      {/* Tactical grid — neutral/800 cells, same asset spec as the empty screens */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `linear-gradient(#262626 1px, transparent 1px), linear-gradient(90deg, #262626 1px, transparent 1px)`,
          backgroundSize: '176px 176px',
          backgroundPosition: 'center',
        }}
      />

      {/* Coordinates HUD */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          p: 4,
          display: { xs: 'none', sm: 'flex' },
          flexDirection: 'column',
          gap: 1.5,
          alignItems: 'flex-end',
        }}
      >
        {CORDS.map((c) => (
          <Box
            key={c.label}
            sx={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end' }}
          >
            <Typography
              sx={{
                fontFamily: osirisTokens.fonts.mono,
                fontSize: 11,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: osirisTokens.text.muted,
              }}
            >
              {c.label}
            </Typography>
            <Box
              sx={{
                height: 24,
                px: 0.5,
                display: 'flex',
                alignItems: 'center',
                borderRadius: '1px',
                bgcolor: 'rgba(0, 0, 0, 0.3)',
              }}
            >
              <Typography
                sx={{
                  fontFamily: osirisTokens.fonts.mono,
                  fontSize: 13,
                  letterSpacing: '0.01em',
                  textTransform: 'uppercase',
                  color: osirisTokens.text.tertiary,
                }}
              >
                {c.value}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Error content */}
      <Box
        sx={{
          position: 'relative',
          width: 530,
          maxWidth: '90vw',
          display: 'flex',
          flexDirection: 'column',
          gap: 5,
          alignItems: 'center',
        }}
      >
        <Box sx={{ position: 'relative', width: '100%', px: 3, py: 2.5 }}>
          <CornerBracket color={tone} sx={{ top: 0, right: 0, borderWidth: '1.5px 1.5px 0 0' }} />
          <CornerBracket color={tone} sx={{ bottom: 0, left: 0, borderWidth: '0 0 1.5px 1.5px' }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography
                sx={{
                  fontFamily: osirisTokens.fonts.mono,
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: '0.55em',
                  textTransform: 'uppercase',
                  color: 'rgba(255, 255, 255, 0.8)',
                }}
              >
                Status:
              </Typography>
              <Typography
                className="error-status-label"
                sx={{
                  fontFamily: osirisTokens.fonts.mono,
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: '0.55em',
                  textTransform: 'uppercase',
                  color: tone,
                }}
              >
                {statusLabel}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography
                className="error-code"
                sx={{
                  fontFamily: osirisTokens.fonts.mono,
                  fontSize: 42,
                  letterSpacing: '0.45em',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                  color: osirisTokens.text.primary,
                }}
              >
                Error {code}
              </Typography>
              <Typography
                sx={{
                  fontFamily: osirisTokens.fonts.mono,
                  fontSize: 22,
                  letterSpacing: '0.25em',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                  color: osirisTokens.text.secondary,
                }}
              >
                {description}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: '2px', width: '100%' }}>
          <Box
            component="button"
            className="error-back-btn"
            onClick={() => navigate(-1)}
            sx={{
              flex: 1,
              height: 40,
              px: 2,
              border: 'none',
              cursor: 'pointer',
              borderRadius: '1px',
              backdropFilter: 'blur(10px)',
              bgcolor: osirisTokens.bg.secondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 120ms ease',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.28)' },
            }}
          >
            <Typography sx={buttonLabelSx}>Back</Typography>
          </Box>
          <Box
            component="button"
            className="error-home-btn"
            onClick={() => navigate('/')}
            sx={{
              flex: 1,
              height: 40,
              px: 2,
              border: 'none',
              cursor: 'pointer',
              borderRadius: '1px',
              bgcolor: osirisTokens.accent.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 120ms ease',
              '&:hover': { bgcolor: osirisTokens.accent.dark },
            }}
          >
            <Typography sx={buttonLabelSx}>Go Home</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export function ErrorPage() {
  const error = useRouteError();
  const is404 = !error || (isRouteErrorResponse(error) && error.status === 404);

  return is404 ? (
    <ErrorView
      code="404"
      statusLabel="Not Found"
      description="Target out of range"
      tone={osirisTokens.accent.mid}
    />
  ) : (
    <ErrorView
      code="500"
      statusLabel="System Failure"
      description="Ground control offline"
      tone={osirisTokens.text.error}
    />
  );
}
